import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes.ts';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyL, BrandM, BrandS } from '@sorare/core/src/atoms/typography';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid';
import { isTypes } from '@sorare/core/src/lib/gql';
import { useCommonGameSet } from '@sorare/core/src/set';

import { SetCollectionTaskPreview } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/SetCollectionTaskPreview/index.tsx';
import { TaskCardPacks } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskCardPacks/index.tsx';
import { TaskCardsOrCollections } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskCardsOrCollections/index.tsx';
import { TaskRewards } from '#src/components/missions/SetCollectionTask/SetCollectionTaskDialog/TaskRewards/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { TaskDetailsDescription } from './TaskDetailsDescription/index.tsx';
import type { SetCollectionTaskDetails_taskInterface } from './__generated__/index.graphql.ts';

const CompetitionBadge = styled(Trapezoid)<{
  $color: string;
}>`
  align-self: center;
  background: ${({ $color }) => $color};
  color: var(--c-white);
`;

const SectionChevron = styled(ChevronDownBold)<{ $expanded?: boolean }>`
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  flex-shrink: 0;
`;

type Props = {
  task: SetCollectionTaskDetails_taskInterface;
};

export const SetCollectionTaskDetails = ({ task }: Props) => {
  const commonGameSet = useCommonGameSet();

  if (
    !isTypes(task, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ])
  ) {
    return null;
  }

  const competitionColor = getTaskCompetitionColor(
    task,
    'var(--c-set-surface-primary)'
  );
  const competitionsDisplayName =
    isTypes(task, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ]) && task.competitions.length > 0
      ? task.competitions.map(({ displayName }) => displayName).join(', ')
      : '';
  const competitionName = competitionsDisplayName || commonGameSet.name;
  const { description } = task;

  return (
    <Vertical gap={3}>
      <CompetitionBadge $color={competitionColor}>
        <BrandM color="var(--c-black)">{competitionName}</BrandM>
      </CompetitionBadge>
      <BodyL className="text-center">{description}</BodyL>
      <SetCollectionTaskPreview
        task={task}
        overrideDescription={<TaskDetailsDescription task={task} />}
      />
      <Vertical style={{ display: 'none' }}>
        <SBHorizontal>
          <Horizontal>
            <Collection size={2} color="var(--c-inventory-yellow)" />
            <BrandS>
              <FormattedMessage defaultMessage="My collection" />
            </BrandS>
          </Horizontal>
          <SectionChevron $expanded={false} />
        </SBHorizontal>
      </Vertical>
      <TaskCardsOrCollections task={task} />
      <TaskRewards task={task} />
      {task.rarity === Rarity.common && <TaskCardPacks task={task} />}
    </Vertical>
  );
};

SetCollectionTaskDetails.fragments = {
  taskInterface: gql`
    fragment SetCollectionTaskDetails_taskInterface on TaskInterface {
      id
      description
      rarity
      ... on CardsCountTargetTask {
        id
        competitions {
          slug
          displayName
        }
      }
      ... on CompleteCollectionsCountTargetTask {
        id
        competitions {
          slug
          displayName
        }
      }
      ...SetCollectionTaskPreview_taskInterface
      ...TaskRewards_taskInterface
      ...TaskCardPacks_taskInterface
      ...getTaskCompetitionColor_taskInterface
      ...TaskDetailsDescription_taskInterface
      ...TaskCardsOrCollections_taskInterface
    }
    ${TaskCardsOrCollections.fragments.taskInterface}
    ${SetCollectionTaskPreview.fragments.taskInterface}
    ${TaskRewards.fragments.taskInterface}
    ${TaskCardPacks.fragments.taskInterface}
    ${getTaskCompetitionColor.fragments.taskInterface}
    ${TaskDetailsDescription.fragments.taskInterface}
  ` as TypedDocumentNode<SetCollectionTaskDetails_taskInterface>,
};
